import { Box, SystemIcon, Typography, Divider } from '@wishket/design-system';

import { twMerge } from 'tailwind-merge';

import ArticleItemImage from '@/features/Article/ArticleItemImage';
import { Link } from '@/shared/Components';
import { PATH } from '@/shared/constants';
import {
  LandingArticleBodyProps,
  LandingArticleInfoProps,
} from '@/shared/types';

const LandingArticleInfo = ({
  category,
  categoryFlag,
  time,
  isPopular,
}: LandingArticleInfoProps) => {
  return (
    <Box className="flex items-center gap-2 overflow-hidden whitespace-nowrap break-keep">
      <Link
        href={`${PATH.LIST}/${categoryFlag}`}
        data-testid="landing-article-category-link"
      >
        <Typography
          variant="body2"
          className="text-w-gray-700 hover:underline"
          data-testid="landing-article-category"
        >
          {category}
        </Typography>
      </Link>
      <Divider isVertical className="h-3.5" />
      <Typography variant="body2" className="text-w-gray-700">
        {time}분
      </Typography>
      {isPopular && (
        <>
          <Divider isVertical className="h-3.5" />
          <Box className="flex items-center gap-1.5">
            <SystemIcon
              name="medium_popular"
              solid
              className="text-w-yellow-500"
            />
            <Typography variant="body2" className="text-w-gray-700">
              인기
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

interface LandingArticleTitleProps {
  title: string;
  href: string;
  titleClassName?: string;
}

const LandingArticleTitle = ({
  title,
  href,
  titleClassName,
}: LandingArticleTitleProps) => {
  return (
    <Link href={href} data-testid="landing-article-title-link">
      <Typography
        variant="body1"
        className={twMerge(
          'line-clamp-2 font-medium text-w-gray-900 hover:text-w-gray-600',
          titleClassName
        )}
        data-testid="landing-article-title-text"
      >
        {title}
      </Typography>
    </Link>
  );
};

const IMAGE_SIZE = {
  sm: {
    className: 'size-[72px] min-w-[72px]',
    value: 72,
  },
  md: {
    className: 'size-[84px] min-w-[84px]',
    value: 84,
  },
} as const;

export const LandingArticleBody = ({
  id,
  title,
  category,
  categoryFlag,
  time,
  isPopular,
  imageUrl,
  titleClassName,
  imageSize = 'sm',
}: LandingArticleBodyProps) => {
  const articleLink = `${PATH.DETAIL}/${id}`;

  return (
    <Box className="group flex justify-between gap-4">
      <Box className="flex h-20 flex-col gap-1 overflow-hidden">
        <LandingArticleTitle
          title={title}
          href={articleLink}
          titleClassName={titleClassName}
        />
        <LandingArticleInfo
          category={category}
          categoryFlag={categoryFlag}
          time={time}
          isPopular={isPopular}
        />
      </Box>
      <Link
        data-testid="landing-article-image-link"
        href={articleLink}
        className={IMAGE_SIZE[imageSize].className}
      >
        <ArticleItemImage
          width={IMAGE_SIZE[imageSize].value}
          height={IMAGE_SIZE[imageSize].value}
          alt={title}
          sizes={`${IMAGE_SIZE[imageSize].value}px`}
          src={imageUrl}
          className={twMerge(
            'rounded-xl border border-w-gray-100 object-cover duration-200 desktop:group-hover:-translate-y-[5%]',
            IMAGE_SIZE[imageSize].className
          )}
          priority
          loading="eager"
        />
      </Link>
    </Box>
  );
};
